import { AxiosError, AxiosResponse } from 'axios';
import { ChangeEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetLoginUserInfo } from '../../../../api/auth/auth';
import type { PostSectionsUpload200, Section, SectionPrivilage } from '../../../../api/model';
import { PsNote } from '../../../../api/model';
import { postSectionsUpload } from '../../../../api/sections/sections';
import { useDefaultOptions } from '../../../../hooks/api';
import { getAxiosRequestConfigForBinary } from '../../../../utils/api';
import SpinnerModal from '../../../modules/SpinnerModal';
import './css/App.css';
import './css/commentCustom.css';
import './css/importantcomment.css';
import './css/myCommentview.css';
import './css/reqspec.css';
import './css/styles.css';
import './css/widget.css';
import CKEditorAdapter from './js/CKEditorAdapter';

type FormValues = {
  fileName: string;
  currentThreadId: string;
  currentCommentId: string;
  currentFsrIds: string;
};

type errorInfo = {
  error_code: string;
  message: string;
  debug_info?: string;
};

type Props = {
  section: Section;
  sectionPrivilege: SectionPrivilage | undefined;
  isReadOnly: boolean;
  isSaveLock: boolean;
  psNotes: PsNote[];
  selectedCharterNumberId: string;
  setEditor: (data: any) => void;
  setPsDerivationId: (psDerivationId: number) => void;
  setSelectedCharterNumberId: (value: string) => void;
  setSecReading: (secReading: boolean) => void;
};
function SectionEditor({
  section,
  sectionPrivilege,
  isReadOnly,
  isSaveLock,
  psNotes,
  selectedCharterNumberId,
  setEditor,
  setPsDerivationId,
  setSelectedCharterNumberId,
  setSecReading,
}: Props) {
  const { productionId } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [okModalMessage, setOkModalMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  // ユーザー情報を取得
  const defaultOptions = useDefaultOptions(language);
  const loginUserInfo = useGetLoginUserInfo(defaultOptions);
  // 原作メーカーのユーザーかどうか
  const isOwnProduction = isSaveLock;

  // api処理中フラグ
  const [apiProcessing, setApiProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    // formState: { errors },
  } = useForm<FormValues, Event>({
    mode: 'onChange',
    criteriaMode: 'all',
    shouldFocusError: false,
  });

  const [argData, SetArgData] = useState({
    argThreadId: '',
    argCommentId: '',
    argCommentURL: '',
    argFileName: '',
  });

  const hiddenInputFile = useRef<HTMLInputElement>(null);
  const threadId = useRef<HTMLInputElement>(null);
  const commentId = useRef<HTMLInputElement>(null);
  const fsrIds = useRef<HTMLInputElement>(null);

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      if (e.target.files) setFile(e.target.files[0]);

      void handleSubmit(async (formData: FormValues) => {
        if (e.target.files) {
          try {
            const axiosConfig = getAxiosRequestConfigForBinary(language);
            const fileName = e.target.files[0].name;
            await postSectionsUpload(Number(productionId), { file: e.target.files[0] }, axiosConfig)
              .then((response: AxiosResponse<PostSectionsUpload200>) => {
                const { data } = response;

                // 何故かBlobになっているのでテキストに変換
                const reader = new FileReader();
                reader.onloadend = () => {
                  SetArgData({
                    ...argData,
                    argThreadId: !threadId.current ? '' : threadId.current.value,
                    argCommentId: !commentId.current ? '' : commentId.current.value,
                    argCommentURL: String(reader.result),
                    argFileName: fileName,
                  });
                };

                // Blobを読み込む
                const blob: Blob = data as Blob;
                reader.readAsText(blob);

                setIsSubmitting(false); // スピナー非表示
                setOkModalMessage('ファイルの添付が終了しました。');
              })
              .catch((resErr: AxiosError) => {
                setIsSubmitting(false); // スピナー非表示

                // エラーメッセージはBlob型の中にある
                if (resErr.response) {
                  const reader = new FileReader();

                  // Blob から jsonに変換
                  reader.onloadend = () => {
                    const ei: errorInfo = JSON.parse(reader.result as string);
                    setOkModalMessage(`ファイルの添付に失敗しました。：${ei.message}`);
                  };

                  // Blobを読み込む
                  const blob: Blob = resErr.response.data as Blob;
                  reader.readAsText(blob);
                }
              });

            // 下記を行わないと連続で同じファイルを取り込めない
            e.target.value = '';
          } catch (er) {
            // 例外が発生した場合の処理
            setIsSubmitting(false); // スピナー非表示
            e.target.value = '';
            setOkModalMessage('不明なエラーが発生しました。');
          }
        }
      })().catch();
    },
    [setIsSubmitting, handleSubmit, language, argData, productionId]
  );

  const [sectionState, setSectionState] = useState<Section | null>(null);
  useEffect(() => {
    setSectionState(null);
    setSectionState(section);
  }, [section, sectionState, setSectionState]);

  /**
   * ORI検索、PS情報、PSメモの権限
   */
  let psInfoIsEnabled = false;
  let oriSearchIsEnabled = false;
  let psNoteIsEnabled = false;
  if (sectionPrivilege !== undefined) {
    psInfoIsEnabled = sectionPrivilege.ps_tab ?? false;
    oriSearchIsEnabled = sectionPrivilege.ori_tab ?? false;
    psNoteIsEnabled = sectionPrivilege.note_tab ?? false;
  }

  return (
    <>
      <div className="editorfr">
        {sectionState && loginUserInfo.data?.data && (
          <CKEditorAdapter
            key={section.title}
            data={argData}
            section={section}
            setEditor={setEditor}
            user={loginUserInfo.data.data}
            isReadOnly={isReadOnly}
            isOwnProduction={isOwnProduction}
            productionId={productionId}
            setPsDerivationId={setPsDerivationId}
            psNotes={psNotes}
            selectedCharterNumberId={selectedCharterNumberId}
            setSelectedCharterNumberId={setSelectedCharterNumberId}
            psInfoIsEnabled={psInfoIsEnabled}
            oriSearchIsEnabled={oriSearchIsEnabled}
            psNoteIsEnabled={psNoteIsEnabled}
            setSecReading={setSecReading}
            setApiProcessing={setApiProcessing} // api処理中フラグ
          />
        )}
        {/* <div className="sidebar" /> */}
        <input
          type="file"
          id="image-select"
          hidden
          {...register('fileName')}
          ref={hiddenInputFile}
          onChange={onChange}
          style={{ display: 'none' }}
        />
        <input type="hidden" id="currentThreadId" hidden {...register('currentThreadId')} ref={threadId} />
        <input type="hidden" id="currentCommentId" hidden {...register('currentCommentId')} ref={commentId} />
        <input type="hidden" id="fsrIds" hidden {...register('currentFsrIds')} ref={fsrIds} />
      </div>
      {isSubmitting && <SpinnerModal />}
      {apiProcessing && <SpinnerModal />}
    </>
  );
}

export { SectionEditor };
